import {
  Modal as BootstrapModal,
  ModalProps as BootrstapModalProps,
} from "solid-bootstrap";
import {
  createSignal,
  ParentComponent,
  ParentProps,
  mergeProps,
  createMemo,
} from "solid-js";

export type ModalState = {
  readonly hidden: boolean;
  hide(): void;
  show(): void;
  toggle(): boolean;
};

const createModalState = (initialState: boolean): ModalState => {
  const [hidden_, setHidden] = createSignal<boolean>(initialState);
  const hidden = createMemo(() => hidden_());
  return {
    get hidden() {
      return hidden();
    },
    toggle() {
      return setHidden((hidden) => !hidden);
    },
    show() {
      let didOpen = false;
      setHidden((wasHidden) => {
        didOpen = wasHidden;
        return false;
      });
      return didOpen;
    },
    hide() {
      let didHide = false;
      setHidden((wasHidden) => {
        didHide = !wasHidden;
        return true;
      });
      return didHide;
    },
  };
};
export type ModalProps = ParentProps &
  Omit<BootrstapModalProps, "show" | "onHide">;
export const createModal = (
  initiallyHidden = true
): [
  modal: ModalState,
  Modal: ParentComponent<BootrstapModalProps> & {
    Body: typeof BootstrapModal.Body;
    Header: typeof BootstrapModal.Header;
    Dialog: typeof BootstrapModal.Dialog;
    Title: typeof BootstrapModal.Title;
    Footer: typeof BootstrapModal.Footer;
  }
] => {
  const modal = createModalState(initiallyHidden);
  const Modal = (props: ModalProps) =>
    BootstrapModal(
      mergeProps(props, {
        get show() {
          return !modal.hidden;
        },
        onHide: () => modal.hide(),
      })
    );
  return [
    modal,
    Object.assign(Modal, {
      Header: BootstrapModal.Header,
      Body: BootstrapModal.Body,
      Dialog: BootstrapModal.Dialog,
      Title: BootstrapModal.Title,
      Footer: BootstrapModal.Footer,
    }),
  ];
};
